/**
 * AT-9296 — pomiar czasu faz po stronie frontu.
 *
 * Znaczniki idą do User Timing API (`performance.mark` / `performance.measure`), czyli na tę
 * samą oś czasu, na której leżą Resource Timing i Web Vitals. Dzięki temu pomiar frontu
 * czyta się razem z siecią i `Server-Timing` backendu, bez osobnego kanału i bez logów.
 *
 * Bramka jest domyślnie ZAMKNIĘTA — w produkcji nie ma narzutu, dopóki ktoś świadomie nie
 * włączy pomiaru. Włączenie: `?perf=1` w adresie (zapisuje się na czas sesji karty) albo
 * `sessionStorage.setItem('at9296-perf', '1')`. Wyłączenie: `?perf=0`.
 *
 * Odczyt w konsoli albo przez CDP:
 *   performance.getEntriesByType('measure').filter(m => m.name.startsWith('at9296:'))
 */

const PREFIX = 'at9296:';
const STORAGE_KEY = 'at9296-perf';

let enabled: boolean | null = null;

/**
 * Czy pomiar jest włączony. Wynik jest liczony raz na dokument: bramka nie może zmieniać
 * się w trakcie, bo połowa faz zmierzona, a połowa nie, jest gorsza niż brak pomiaru.
 */
export function perfEnabled(): boolean {
  if (enabled !== null) {
    return enabled;
  }

  // SSR: brak `window` i `sessionStorage`. Pomiar faz dotyczy przeglądarki, więc po stronie
  // serwera bramka jest po prostu zamknięta — czasy SSR mierzy instrumentacja w `server.ts`.
  if (typeof window === 'undefined' || typeof performance === 'undefined') {
    enabled = false;
    return enabled;
  }

  try {
    const param = new URLSearchParams(window.location.search).get('perf');

    if (param === '1') {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } else if (param === '0') {
      sessionStorage.removeItem(STORAGE_KEY);
    }

    enabled = sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch (e) {
    // Prywatne okno albo zablokowane dane witryny — brak pomiaru jest poprawnym stanem.
    enabled = false;
  }

  return enabled;
}

/** Punkt na osi czasu. Sam w sobie nic nie mierzy — służy za początek dla `perfMeasure`. */
export function perfMark(name: string): void {
  if (!perfEnabled()) {
    return;
  }

  try {
    performance.mark(PREFIX + name);
  } catch (e) {
    // Pomiar nigdy nie może wywrócić aplikacji.
  }
}

/**
 * Odcinek od znacznika `from` do teraz. Gdy znacznika początkowego nie ma (np. faza nie
 * wystartowała, bo użytkownik wszedł w środek przepływu), mierzymy od początku nawigacji —
 * inaczej `measure` rzuca i tracimy także te fazy, które dało się zmierzyć.
 */
export function perfMeasure(name: string, from: string): void {
  if (!perfEnabled()) {
    return;
  }

  try {
    const start = performance.getEntriesByName(PREFIX + from, 'mark').length ? PREFIX + from : undefined;
    performance.measure(PREFIX + name, start);
  } catch (e) {
    // jw.
  }
}

/**
 * Faza o znanym początku i końcu. Zwraca domknięcie kończące pomiar, żeby wywołujący nie
 * musiał pilnować nazw dwóch znaczników:
 *
 *   const endRender = perfPhase('render-blokow');
 *   ...
 *   endRender();
 */
export function perfPhase(name: string): () => void {
  if (!perfEnabled()) {
    return () => undefined;
  }

  const start = `${name}-start`;
  perfMark(start);

  return () => perfMeasure(name, start);
}

/**
 * Wszystkie zmierzone odcinki, posortowane po czasie rozpoczęcia. Do odczytu z konsoli
 * i przez CDP; nazwy są zwracane bez prefiksu, bo prefiks służy tylko filtrowaniu.
 */
export function perfReport(): Array<{ faza: string; start: number; ms: number }> {
  if (typeof performance === 'undefined') {
    return [];
  }

  return performance
    .getEntriesByType('measure')
    .filter((m) => m.name.startsWith(PREFIX))
    .map((m) => ({ faza: m.name.slice(PREFIX.length), start: Math.round(m.startTime), ms: Math.round(m.duration) }))
    .sort((a, b) => a.start - b.start);
}
